import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { 
    CreditCard, DollarSign, Receipt, Wallet, Smartphone,
    RefreshCcw, Play, Lock, Unlock, X, LogOut,
    ShoppingCart, Clock, CheckCircle, AlertCircle,
    Banknote, Eye, Plus, ChevronRight, Calculator,
    User, Timer, History, TrendingUp, Package
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// ========================================
// UTILITY FUNCTIONS
// ========================================

const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return Math.round(num).toLocaleString('fr-FR').replace(/,/g, ' ');
};

const formatCurrency = (amount) => formatNumber(amount) + ' F';

const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('fr-FR', {
        timeZone: 'Africa/Libreville',
        hour: '2-digit',
        minute: '2-digit'
    });
};

// ========================================
// REGISTER STATUS BANNER
// ========================================

const RegisterStatusBanner = ({ status, registerName, onAction }) => {
    const statusConfig = {
        'no_register': {
            bg: 'bg-gray-100',
            border: 'border-gray-300',
            text: 'text-gray-700',
            icon: AlertCircle,
            title: 'Aucune caisse attribuée',
            description: 'Contactez votre responsable pour obtenir une attribution de caisse.',
            action: null
        },
        'ready_to_open': {
            bg: 'bg-blue-50',
            border: 'border-blue-300',
            text: 'text-blue-700',
            icon: CreditCard,
            title: `Caisse "${registerName}" prête`,
            description: 'Votre caisse vous attend. Ouvrez-la pour commencer à vendre.',
            action: { label: 'Ouvrir ma caisse', variant: 'default', onClick: () => onAction('open') }
        },
        'open': {
            bg: 'bg-green-50',
            border: 'border-green-300',
            text: 'text-green-700',
            icon: CheckCircle,
            title: `Caisse "${registerName}" ouverte`,
            description: 'Vous êtes prêt à encaisser. Bonne vente !',
            action: { label: 'Aller à la vente', variant: 'default', onClick: () => onAction('pos'), className: 'bg-green-600 hover:bg-green-700' }
        },
        'locked': {
            bg: 'bg-amber-50',
            border: 'border-amber-300',
            text: 'text-amber-700',
            icon: Lock,
            title: `Caisse "${registerName}" verrouillée`,
            description: 'Déverrouillez votre caisse pour reprendre les ventes.',
            action: { label: 'Déverrouiller', variant: 'default', onClick: () => onAction('unlock'), className: 'bg-amber-600 hover:bg-amber-700' }
        },
        'closed': {
            bg: 'bg-gray-100',
            border: 'border-gray-300',
            text: 'text-gray-600',
            icon: X,
            title: `Caisse "${registerName}" fermée`,
            description: 'Cette caisse a été clôturée.',
            action: null
        }
    };
    
    const config = statusConfig[status] || statusConfig['no_register'];
    const StatusIcon = config.icon;
    
    return (
        <div className={cn(
            "p-4 rounded-2xl border-2",
            config.bg, config.border
        )}>
            <div className="flex items-start gap-4">
                <div className={cn(
                    "p-3 rounded-xl",
                    status === 'open' && "bg-green-100",
                    status === 'locked' && "bg-amber-100",
                    status === 'ready_to_open' && "bg-blue-100",
                    (status === 'no_register' || status === 'closed') && "bg-gray-200"
                )}>
                    <StatusIcon className={cn("w-8 h-8", config.text)} />
                </div>
                <div className="flex-1">
                    <h2 className={cn("text-lg font-bold", config.text)}>{config.title}</h2>
                    <p className="text-sm text-muted-foreground mt-1">{config.description}</p>
                    {config.action && (
                        <Button 
                            className={cn("mt-3 h-12 px-6 text-base", config.action.className)}
                            onClick={config.action.onClick}
                        >
                            {status === 'open' && <ShoppingCart className="w-5 h-5 mr-2" />}
                            {status === 'locked' && <Unlock className="w-5 h-5 mr-2" />}
                            {status === 'ready_to_open' && <Play className="w-5 h-5 mr-2" />}
                            {config.action.label}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

// ========================================
// STAT CARD
// ========================================

const StatCard = ({ title, value, subtitle, icon: Icon, status = 'default', large }) => {
    const colors = {
        green: 'bg-green-50 border-green-200 text-green-700',
        blue: 'bg-blue-50 border-blue-200 text-blue-700',
        amber: 'bg-amber-50 border-amber-200 text-amber-700',
        purple: 'bg-purple-50 border-purple-200 text-purple-700',
        cyan: 'bg-cyan-50 border-cyan-200 text-cyan-700',
        default: 'bg-white border-gray-200 text-gray-700'
    };
    
    const iconColors = {
        green: 'bg-green-100 text-green-600',
        blue: 'bg-blue-100 text-blue-600',
        amber: 'bg-amber-100 text-amber-600',
        purple: 'bg-purple-100 text-purple-600',
        cyan: 'bg-cyan-100 text-cyan-600',
        default: 'bg-gray-100 text-gray-600'
    };
    
    return (
        <div className={cn(
            "p-4 rounded-xl border-2 transition-all",
            colors[status],
            large && "p-5"
        )}>
            <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <p className={cn("text-xs font-medium opacity-70", large && "text-sm")}>{title}</p>
                    <p className={cn("font-bold truncate", large ? "text-3xl" : "text-xl")}>{value}</p>
                    {subtitle && <p className="text-xs opacity-60 mt-0.5">{subtitle}</p>}
                </div>
                {Icon && (
                    <div className={cn(
                        "rounded-xl flex items-center justify-center shrink-0",
                        large ? "w-12 h-12" : "w-10 h-10",
                        iconColors[status]
                    )}>
                        <Icon className={cn(large ? "w-6 h-6" : "w-5 h-5")} />
                    </div>
                )}
            </div>
        </div>
    );
};

// ========================================
// TICKET ITEM
// ========================================

const TicketItem = ({ ticket, onClick }) => {
    return (
        <div 
            className="p-3 bg-white rounded-xl border hover:shadow-md transition-all cursor-pointer"
            onClick={() => onClick?.(ticket)}
        >
            <div className="flex items-center justify-between mb-2">
                <Badge variant="outline" className="text-xs">
                    #{ticket.ticket_number || ticket.id?.slice(-6)}
                </Badge>
                <span className="text-xs text-muted-foreground">
                    {formatTime(ticket.created_at)}
                </span>
            </div>
            <div className="flex items-center justify-between">
                <div className="text-sm">
                    <span className="text-muted-foreground">{ticket.items_count || 0} article(s)</span>
                    {ticket.table_number && (
                        <span className="ml-2 text-muted-foreground">• Table {ticket.table_number}</span>
                    )}
                </div>
                <span className="font-bold text-green-600">{formatCurrency(ticket.total)}</span>
            </div>
            <div className="mt-2 flex items-center gap-2">
                <Badge className={cn(
                    "text-[10px]",
                    ticket.payment_method === 'cash' && "bg-green-500",
                    ticket.payment_method === 'card' && "bg-blue-500",
                    ticket.payment_method === 'mobile' && "bg-purple-500"
                )}>
                    {ticket.payment_method === 'cash' && 'Espèces'}
                    {ticket.payment_method === 'card' && 'Carte'}
                    {ticket.payment_method === 'mobile' && 'Mobile'}
                </Badge>
            </div>
        </div>
    );
};

// ========================================
// QUICK ACTION BUTTON
// ========================================

const QuickActionButton = ({ icon: Icon, label, onClick, disabled, variant = 'default', className }) => {
    const variants = {
        default: 'bg-white border-gray-200 hover:bg-gray-50',
        primary: 'bg-green-600 text-white border-green-600 hover:bg-green-700',
        warning: 'bg-amber-600 text-white border-amber-600 hover:bg-amber-700',
        danger: 'bg-red-600 text-white border-red-600 hover:bg-red-700'
    };
    
    return (
        <button
            className={cn(
                "flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all active:scale-95",
                variants[variant],
                disabled && "opacity-50 cursor-not-allowed",
                className
            )}
            onClick={onClick}
            disabled={disabled}
        >
            <Icon className="w-6 h-6 mb-2" />
            <span className="text-xs font-medium text-center">{label}</span>
        </button>
    );
};

// ========================================
// MAIN CASHIER DASHBOARD
// ========================================

const CashierDashboardPage = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    
    // State
    const [loading, setLoading] = useState(true);
    const [registerStatus, setRegisterStatus] = useState(null);
    const [todayStats, setTodayStats] = useState({
        total_sales: 0,
        tickets_count: 0,
        cash_total: 0,
        card_total: 0,
        mobile_total: 0,
        opening_float: 0,
        theoretical_total: 0
    });
    const [recentTickets, setRecentTickets] = useState([]);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    
    // Dialogs
    const [unlockDialog, setUnlockDialog] = useState(false);
    const [unlockPin, setUnlockPin] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [openingFloat, setOpeningFloat] = useState('');
    const [processing, setProcessing] = useState(false);
    
    const intervalRef = useRef(null);
    
    // ========================================
    // DATA FETCHING
    // ========================================
    
    const fetchData = useCallback(async (showToast = false) => {
        try {
            const [statusRes, ticketsRes] = await Promise.all([
                api().get('/registers/my-status').catch(() => ({ data: null })),
                api().get('/pos/my-tickets?limit=10').catch(() => ({ data: [] }))
            ]);
            
            const status = statusRes.data;
            setRegisterStatus(status);
            
            // Calculate today stats from status
            if (status) {
                setTodayStats({
                    total_sales: status.total_sales || 0,
                    tickets_count: status.orders_count || 0,
                    cash_total: status.cash_total || 0,
                    card_total: status.card_total || 0,
                    mobile_total: status.mobile_total || 0,
                    opening_float: status.opening_float || 0,
                    theoretical_total: (status.opening_float || 0) + (status.cash_total || 0)
                });
            }
            
            setRecentTickets(ticketsRes.data || []);
            setLastUpdate(new Date());
            
            if (showToast) {
                toast.success('Données actualisées');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [api]);
    
    // Initial load
    useEffect(() => {
        fetchData();
    }, []);
    
    // Auto-refresh every 10 seconds
    useEffect(() => {
        if (autoRefresh) {
            intervalRef.current = setInterval(() => fetchData(false), 10000);
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [autoRefresh, fetchData]);
    
    // ========================================
    // COMPUTED VALUES
    // ========================================
    
    const getRegisterStatusKey = () => {
        if (!registerStatus) return 'no_register';
        if (registerStatus.status === 'closed') return 'closed';
        if (registerStatus.is_locked) return 'locked';
        if (registerStatus.status === 'open') return 'open';
        if (registerStatus.register_id && registerStatus.status !== 'open') return 'ready_to_open';
        return 'no_register';
    };
    
    const statusKey = getRegisterStatusKey();
    const isOpen = statusKey === 'open';
    const isLocked = statusKey === 'locked';
    const canSell = isOpen && !isLocked;
    
    // ========================================
    // HANDLERS
    // ========================================
    
    const handleAction = (action) => {
        switch (action) {
            case 'open':
                setOpeningFloat('');
                setOpenDialog(true);
                break;
            case 'unlock':
                setUnlockPin('');
                setUnlockDialog(true);
                break;
            case 'pos':
                navigate('/pos');
                break;
            default:
                break;
        }
    };
    
    const handleOpenRegister = async () => {
        if (!registerStatus?.register_id) return;
        
        setProcessing(true);
        try {
            await api().post('/registers/open-session', {
                register_id: registerStatus.register_id,
                opening_float: parseFloat(openingFloat) || 0
            });
            toast.success('Caisse ouverte');
            setOpenDialog(false);
            fetchData();
            // Go directly to POS
            navigate('/pos');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'ouverture');
        } finally {
            setProcessing(false);
        }
    };
    
    const handleUnlockRegister = async () => {
        if (!unlockPin || !registerStatus?.session_id) {
            toast.error('Veuillez saisir votre PIN');
            return;
        }
        
        setProcessing(true);
        try {
            await api().post(`/registers/${registerStatus.session_id}/unlock`, {
                pin: unlockPin
            });
            toast.success('Caisse déverrouillée');
            setUnlockDialog(false);
            setUnlockPin('');
            // Go directly to POS after unlock
            navigate('/pos');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'PIN incorrect');
        } finally {
            setProcessing(false);
        }
    };
    
    const handleLockRegister = async () => {
        if (!registerStatus?.session_id) return;
        
        try {
            await api().post(`/registers/${registerStatus.session_id}/lock`);
            toast.success('Caisse verrouillée');
            fetchData();
        } catch (error) {
            toast.error('Erreur lors du verrouillage');
        }
    };
    
    const handleCloseSession = async () => {
        if (!registerStatus?.session_id) return;
        
        // Navigate to cash closing page
        navigate('/cash-closing');
    };
    
    const handleTicketClick = (ticket) => {
        // Could open ticket details
        toast.info(`Ticket #${ticket.ticket_number || ticket.id?.slice(-6)}`);
    };
    
    // ========================================
    // RENDER
    // ========================================
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-[60vh]">
                <RefreshCcw className="w-8 h-8 animate-spin text-green-600" />
                <span className="ml-3 text-muted-foreground">Chargement...</span>
            </div>
        );
    }

    return (
        <div className="p-2 sm:p-4 lg:p-6 space-y-4 max-w-[1200px] mx-auto" data-testid="cashier-dashboard">
            {/* ========================================
                HEADER
            ======================================== */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                        <CreditCard className="w-6 h-6 text-green-600" />
                        Ma Caisse
                    </h1>
                    <p className="text-xs text-muted-foreground">
                        {user?.name || 'Caissier'}
                        {lastUpdate && ` • ${formatTime(lastUpdate.toISOString())}`}
                    </p>
                </div>
                <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => fetchData(true)}
                    className="h-10"
                >
                    <RefreshCcw className={cn("w-4 h-4 mr-1", loading && "animate-spin")} />
                    Actualiser
                </Button>
            </div>

            {/* ========================================
                REGISTER STATUS BANNER
            ======================================== */}
            <RegisterStatusBanner 
                status={statusKey}
                registerName={registerStatus?.register_name || 'Caisse'}
                onAction={handleAction}
            />

            {/* ========================================
                STATS ROW - Only if register is open
            ======================================== */}
            {(isOpen || isLocked) && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                    <StatCard
                        title="Ventes du jour"
                        value={formatCurrency(todayStats.total_sales)}
                        icon={TrendingUp}
                        status="green"
                        large
                    />
                    <StatCard
                        title="Tickets"
                        value={todayStats.tickets_count}
                        icon={Receipt}
                        status="blue"
                    />
                    <StatCard
                        title="Espèces"
                        value={formatCurrency(todayStats.cash_total)}
                        icon={Banknote}
                        status="green"
                    />
                    <StatCard
                        title="Carte"
                        value={formatCurrency(todayStats.card_total)}
                        icon={CreditCard}
                        status="blue"
                    />
                    <StatCard
                        title="Mobile Money"
                        value={formatCurrency(todayStats.mobile_total)}
                        icon={Smartphone}
                        status="purple"
                    />
                    <StatCard
                        title="Fond de caisse"
                        value={formatCurrency(todayStats.opening_float)}
                        icon={Wallet}
                        status="amber"
                    />
                </div>
            )}

            {/* ========================================
                MAIN CONTENT
            ======================================== */}
            {(isOpen || isLocked) && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* COLUMN 1-2: Quick Actions + POS Access */}
                    <div className="lg:col-span-2 space-y-4">
                        {/* Main Action */}
                        <Card>
                            <CardContent className="p-4">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <QuickActionButton
                                        icon={ShoppingCart}
                                        label="Nouvelle vente"
                                        variant="primary"
                                        onClick={() => navigate('/pos')}
                                        disabled={!canSell}
                                        className="col-span-2 sm:col-span-1 h-24"
                                    />
                                    <QuickActionButton
                                        icon={History}
                                        label="Historique"
                                        onClick={() => navigate('/daily-sales')}
                                        className="h-24"
                                    />
                                    {isLocked ? (
                                        <QuickActionButton
                                            icon={Unlock}
                                            label="Déverrouiller"
                                            variant="warning"
                                            onClick={() => handleAction('unlock')}
                                            className="h-24"
                                        />
                                    ) : (
                                        <QuickActionButton
                                            icon={Lock}
                                            label="Verrouiller"
                                            onClick={handleLockRegister}
                                            className="h-24"
                                        />
                                    )}
                                    <QuickActionButton
                                        icon={LogOut}
                                        label="Clôturer"
                                        variant="danger"
                                        onClick={handleCloseSession}
                                        className="h-24"
                                    />
                                </div>
                            </CardContent>
                        </Card>
                        
                        {/* Cash Summary */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Calculator className="w-4 h-4 text-green-600" />
                                    Résumé de caisse
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                                        <span className="text-sm text-amber-700">Fond de caisse</span>
                                        <span className="font-bold text-amber-700">{formatCurrency(todayStats.opening_float)}</span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                        <span className="text-sm text-green-700">Encaissements espèces</span>
                                        <span className="font-bold text-green-700">+ {formatCurrency(todayStats.cash_total)}</span>
                                    </div>
                                    <div className="h-px bg-gray-200" />
                                    <div className="flex items-center justify-between p-3 bg-green-100 rounded-lg">
                                        <span className="text-sm font-medium text-green-800">Total théorique en caisse</span>
                                        <span className="font-bold text-lg text-green-800">{formatCurrency(todayStats.theoretical_total)}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    
                    {/* COLUMN 3: Recent Tickets */}
                    <Card>
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Receipt className="w-4 h-4 text-blue-600" />
                                    Derniers tickets
                                </CardTitle>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => navigate('/daily-sales')}
                                >
                                    <Eye className="w-4 h-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-2">
                            <ScrollArea className="h-[350px]">
                                {recentTickets.length === 0 ? (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                        <p className="text-sm">Aucun ticket aujourd'hui</p>
                                        <p className="text-xs">Commencez à vendre !</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2 p-2">
                                        {recentTickets.map(ticket => (
                                            <TicketItem 
                                                key={ticket.id}
                                                ticket={ticket}
                                                onClick={handleTicketClick}
                                            />
                                        ))}
                                    </div>
                                )}
                            </ScrollArea>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* ========================================
                NO REGISTER OR CLOSED - Show message
            ======================================== */}
            {(statusKey === 'no_register' || statusKey === 'closed' || statusKey === 'ready_to_open') && (
                <div className="text-center py-12">
                    <CreditCard className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg text-muted-foreground mb-2">
                        {statusKey === 'no_register' && "En attente d'une attribution de caisse"}
                        {statusKey === 'ready_to_open' && "Ouvrez votre caisse pour commencer"}
                        {statusKey === 'closed' && "Votre caisse est fermée"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                        {statusKey === 'no_register' && "Contactez votre responsable pour obtenir l'accès à une caisse."}
                        {statusKey === 'ready_to_open' && "Cliquez sur le bouton ci-dessus pour ouvrir votre caisse."}
                        {statusKey === 'closed' && "Cette session de caisse est terminée."}
                    </p>
                </div>
            )}

            {/* ========================================
                FIXED ACTION BAR (only when open)
            ======================================== */}
            {canSell && (
                <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
                    <Button 
                        size="lg" 
                        className="rounded-full bg-green-600 hover:bg-green-700 h-14 px-8 text-lg shadow-xl"
                        onClick={() => navigate('/pos')}
                    >
                        <Plus className="w-6 h-6 mr-2" />
                        Nouvelle vente
                    </Button>
                </div>
            )}
            
            {/* Bottom padding */}
            <div className="h-20" />

            {/* ========================================
                DIALOG: Open Register
            ======================================== */}
            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="text-xl flex items-center gap-2">
                            <Play className="w-6 h-6 text-green-600" />
                            Ouvrir ma caisse
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <label className="text-sm font-medium mb-2 block">Fond de caisse (optionnel)</label>
                        <div className="relative">
                            <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <Input
                                type="number"
                                placeholder="0"
                                value={openingFloat}
                                onChange={(e) => setOpeningFloat(e.target.value)}
                                className="pl-12 h-14 text-xl"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">F CFA</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            Saisissez le montant d'espèces présent dans la caisse au démarrage.
                        </p>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setOpenDialog(false)} className="flex-1 h-12">
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleOpenRegister} 
                            className="flex-1 h-12 bg-green-600 hover:bg-green-700"
                            disabled={processing}
                        >
                            {processing ? (
                                <RefreshCcw className="w-5 h-5 mr-2 animate-spin" />
                            ) : (
                                <Play className="w-5 h-5 mr-2" />
                            )}
                            Ouvrir
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ========================================
                DIALOG: Unlock Register
            ======================================== */}
            <Dialog open={unlockDialog} onOpenChange={setUnlockDialog}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="text-xl flex items-center gap-2">
                            <Unlock className="w-6 h-6 text-amber-600" />
                            Déverrouiller ma caisse
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <label className="text-sm font-medium mb-2 block">Votre code PIN</label>
                        <Input
                            type="password"
                            placeholder="••••"
                            value={unlockPin}
                            onChange={(e) => setUnlockPin(e.target.value)}
                            className="h-14 text-2xl text-center tracking-widest"
                            maxLength={6}
                        />
                        <p className="text-xs text-muted-foreground mt-2 text-center">
                            Saisissez votre code PIN personnel
                        </p>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setUnlockDialog(false)} className="flex-1 h-12">
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleUnlockRegister} 
                            className="flex-1 h-12 bg-amber-600 hover:bg-amber-700"
                            disabled={processing || unlockPin.length < 4}
                        >
                            {processing ? (
                                <RefreshCcw className="w-5 h-5 mr-2 animate-spin" />
                            ) : (
                                <Unlock className="w-5 h-5 mr-2" />
                            )}
                            Déverrouiller
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CashierDashboardPage;
